import React, { useMemo } from 'react';
import { ProductSummary } from '../../../types';

interface MergedRow {
  productId: string;
  productName: string;
  unit: string;
  soldQty: number;
  producedQty: number;
}

type Status = 'match' | 'overproduction' | 'underproduction' | 'not-started' | 'production-only';

function getStatus(row: MergedRow): Status {
  if (row.soldQty === 0) return 'production-only';
  if (row.producedQty === 0) return 'not-started';
  const diff = row.producedQty - row.soldQty;
  if (diff === 0) return 'match';
  return diff > 0 ? 'overproduction' : 'underproduction';
}

const STYLES: Record<Status, { card: string; badge: string; label: string; numColor: string }> = {
  match:           { card: 'border-green-300 bg-green-50',  badge: 'bg-green-100 text-green-700',  label: 'Completado',      numColor: 'text-green-700'  },
  overproduction:  { card: 'border-amber-300 bg-amber-50',  badge: 'bg-amber-100 text-amber-700',  label: 'Sobreproducción', numColor: 'text-amber-700'  },
  underproduction: { card: 'border-blue-300 bg-blue-50',    badge: 'bg-blue-100 text-blue-700',    label: 'Pendiente',       numColor: 'text-blue-700'   },
  'not-started':   { card: 'border-red-300 bg-red-50',      badge: 'bg-red-100 text-red-700',      label: 'Sin producir',    numColor: 'text-red-600'    },
  'production-only': { card: 'border-slate-200 bg-slate-50', badge: 'bg-slate-100 text-slate-600', label: 'Sin pedido',      numColor: 'text-slate-600'  },
};

interface ProductComparisonViewProps {
  productionSummary: ProductSummary[];
  ordersSummary: ProductSummary[];
  onProductClick: (productId: string, productName: string) => void;
}

export const ProductComparisonView: React.FC<ProductComparisonViewProps> = ({
  productionSummary,
  ordersSummary,
  onProductClick,
}) => {
  const rows = useMemo<MergedRow[]>(() => {
    const map = new Map<string, MergedRow>();

    // Seed from orders (the "target")
    ordersSummary.forEach(p => {
      p.quantities.forEach(q => {
        map.set(`${p.productId}-${q.unit}`, {
          productId: p.productId,
          productName: p.productName,
          unit: q.unit,
          soldQty: q.quantity,
          producedQty: 0,
        });
      });
    });

    // Fill produced quantities
    productionSummary.forEach(p => {
      p.quantities.forEach(q => {
        const key = `${p.productId}-${q.unit}`;
        const row = map.get(key);
        if (row) {
          row.producedQty = q.quantity;
        } else {
          map.set(key, {
            productId: p.productId,
            productName: p.productName,
            unit: q.unit,
            soldQty: 0,
            producedQty: q.quantity,
          });
        }
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.productName.localeCompare(b.productName, 'es', { sensitivity: 'base' })
    );
  }, [productionSummary, ordersSummary]);

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
        No hay datos para este período
      </div>
    );
  }

  return (
    <div className="@container space-y-1.5">
      {rows.map((row, idx) => {
        const status = getStatus(row);
        const s = STYLES[status];
        const diff = row.producedQty - row.soldQty;

        return (
          <div
            key={idx}
            onClick={() => onProductClick(row.productId, row.productName)}
            className={`rounded-lg border-2 p-2.5 cursor-pointer active:scale-[0.99] transition-transform select-none ${s.card} @lg:py-2 @lg:px-3`}
          >
            <div className="@lg:flex @lg:items-center @lg:gap-6 @lg:max-w-2xl @lg:mx-auto @lg:w-full">
              {/* Header (name + badge) */}
              <div className="flex items-start justify-between gap-2 mb-2 @lg:mb-0 @lg:flex-1 @lg:min-w-0 @lg:flex-col @lg:items-start @lg:justify-start @lg:gap-1">
                <span className="font-bold text-slate-900 text-base leading-tight">{row.productName}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${s.badge}`}>
                  {s.label}
                </span>
              </div>

              {/* Three numbers */}
              <div className="grid grid-cols-3 text-center @lg:flex @lg:gap-8 @lg:shrink-0">
                <div className="@lg:text-right">
                  <div className="text-2xl font-black text-slate-700 leading-none tabular-nums">{row.soldQty}</div>
                  <div className="text-xs text-slate-500 mt-0.5">vendidas</div>
                  <div className="text-xs text-slate-400">{row.unit}</div>
                </div>
                <div className="@lg:text-right">
                  <div className={`text-2xl font-black leading-none tabular-nums ${s.numColor}`}>{row.producedQty}</div>
                  <div className="text-xs text-slate-500 mt-0.5">producidas</div>
                  <div className="text-xs text-slate-400">{row.unit}</div>
                </div>
                <div className="@lg:text-right">
                  <div className={`text-2xl font-black leading-none tabular-nums ${s.numColor}`}>
                    {diff === 0 ? '✓' : diff > 0 ? `+${diff}` : String(Math.abs(diff))}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {diff === 0 ? 'exacto' : diff > 0 ? 'sobran' : 'faltan'}
                  </div>
                  <div className="text-xs text-slate-400">{diff !== 0 ? row.unit : ''}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
